import { QueryKeys } from "@/constants/query-keys";
import { ONBOARDING_TRANSITION_DURATION_MS } from "@/features/user-profile/constants/onboarding-transition";
import { delay } from "@/features/user-profile/utils/delay";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";
import { SelectablePill } from "../../components/selectable-pill";

const GENDER_OPTIONS = [
  { id: "female", label: "Female" },
  { id: "male", label: "Male" },
];

const OnboardingGenderPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { user } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const personalInfo = user?.personalInfo;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = async (sex: string) => {
    if (isUpdating) return;
    setSelectedId(sex);
    await delay(ONBOARDING_TRANSITION_DURATION_MS);
    try {
      await Promise.all([
        preloadNextRoute(),
        updateUserAsync({
          firstName: personalInfo?.firstName ?? "",
          lastName: personalInfo?.lastName ?? "",
          dob: personalInfo?.dob ?? "",
          sex,
        }),
      ]);
      queryClient.setQueryData([QueryKeys.USER], (old: object) => ({
        ...old,
        personalInfo: { ...personalInfo, sex },
      }));
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/gender.svg"
          title="What’s your gender?"
          subtitle="We need the gender you were assigned at birth to build your plan"
        >
          <div className="flex w-full flex-col items-center gap-6">
            {GENDER_OPTIONS.map((option) => (
              <SelectablePill
                key={option.id}
                label={option.label}
                variant={selectedId === option.id ? "active" : "default"}
                onClick={() => handleSelect(option.id)}
                disabled={isUpdating}
              />
            ))}
          </div>
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingGenderPage;
